<template>
  <div class="asc-custom-alerts-grid--wrapper">
    <asc-ag-grid-filter
      :key="`asc-analytic-grid_${gridId}`"
      class="asc-custom-alerts-grid"
      :grid-options="agGridOptions"
      :column-defs="columnDefs"
      :row-data="agGridRowData"
    />
    <div v-if="!agGridRowData.length" class="asc-customAlertsNoData--wrapper">
      <asc-custom-alerts-no-data
        @openAlertTypeDialog="openAlertTypeDialogHandler"
      ></asc-custom-alerts-no-data>
    </div>
  </div>
</template>

<script>
import { AscGridPropsMixin, AscGridMethodsMixin } from '@/components/mixins'
import AscCustomAlertsNoData from '@/components/atoms/AscCustomAlertsNoData'
import { getFilterOptions } from '@/components/agGrid/AgGridOptions'
import AscTooltip from '@/components/atoms/AscTooltip'
import {
  CUSTOM_ALERT_FULL_FLIGHT_DATA_ID,
  CUSTOM_ALERT_SNAPSHOT_DATA_ID,
  ADVANCED_CUSTOM_ALERTS_SNAPSHOT_DATA_ID,
  ADVANCED_CUSTOM_ALERTS_FULL_FLIGHT_DATA_ID,
  ADVANCED_CUSTOM_ALERTS_DPLF_DATA_ID,
} from '@/utilities/appConsts'
import {
  AscAgGridFilter,
  AscAgGridAutocompleteFloatFilter,
  AscLinkCellRenderer,
  AscAlertSwitchCellRenderer,
  AscAgGridAutocompleteNumberFilter,
  AscAgGridEllipseRenderer,
} from '@/components/agGrid'
import { stringTextMatcher } from '@/components/agGrid/AgGridHelperMethods'
import AscAgTooltip from '@/components/atoms/AscAgTooltip'

export default {
  name: 'AscCustomAlertsGrid',

  components: {
    AscAgGridFilter,
    AscCustomAlertsNoData,
    AscLinkCellRenderer,
    AscAlertSwitchCellRenderer,
    AscAgGridAutocompleteFloatFilter,
    AscAgGridEllipseRenderer,
    AscAgTooltip,
    AscTooltip,
    AscAgGridAutocompleteNumberFilter,
  },

  mixins: [AscGridPropsMixin, AscGridMethodsMixin],

  props: {
    disableSnapShotToggles: {
      type: Boolean,
      default: false,
    },
    disableFullFlightToggles: {
      type: Boolean,
      default: false,
    },
    isSharedTab: {
      type: Boolean,
      default: false,
    },
    userAirlines: {
      type: Array,
      default: () => [],
    },
    sharedAlerts: {
      type: Array,
      default: () => [],
    },
    user: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      agGridOptions: getFilterOptions(this, {
        defaultColDef: {
          tooltipComponent: 'AscAgTooltip',
        },
        rowSelection: 'multiple',
        suppressCellFocus: false,
        suppressRowClickSelection: true,
        rowMultiSelectWithClick: true,
        onGridReady: this.handleGridReady,
        onGridSizeChanged: this.handleGridSizeChanged,
        noRowsOverlayComponent: this.noOverlayComponent,
        getContextMenuItems: this.getContextMenuItems,
      }),
      CUSTOM_ALERT_FULL_FLIGHT_DATA_ID,
      CUSTOM_ALERT_SNAPSHOT_DATA_ID,
      ADVANCED_CUSTOM_ALERTS_SNAPSHOT_DATA_ID,
      columnDefs: this.getColumnDefs(),
      isTooltipVisible: false,
    }
  },

  computed: {
    mappedValues() {
      return this.values.map(value => {
        return {
          ...value,
          shared: this.populateSharedWith(value),
          truePositives: this.populateTruePositive(value),
          falsePositives: this.populateFalsePositive(value),
          precision: this.populatePrecision(value),
        }
      })
    },

    mappedSharedValues() {
      return this.sharedAlerts.map(value => {
        return {
          ...value,
          truePositives: this.populateTruePositive(value),
          falsePositives: this.populateFalsePositive(value),
          precision: this.populatePrecision(value),
        }
      })
    },

    agGridRowData() {
      this.resetLocalStorage(this.localStorageName)
      if (this.isSharedTab) {
        return this.mappedSharedValues
      }
      return this.mappedValues
    },
    sharedWithFilterRows() {
      return this.setSharedWithFilter()
    },
    hideOnSharedTab() {
      if (this.isSharedTab) {
        return true
      }
      return false
    },
    setReadonly() {
      if (this.isSharedTab) {
        return true
      }
      return false
    },
  },

  watch: {
    values() {
      this.columnDefs = this.getColumnDefs()
      if (this.gridApi) this.gridApi.refreshHeader()
      this.handleRefreshColWidths()
    },
  },

  methods: {
    getColumnDefs() {
      return [
        {
          headerName: this.getColumnTitle(this.gridColumns, 'active', 'Active'),
          field: 'active',
          width: 120,
          suppressMovable: true,
          resizable: false,
          sortable: true,
          suppressSizeToFit: true,
          cellRenderer: 'AscAlertSwitchCellRenderer',
          cellRendererParams: {
            readonly: this.setReadonly,
            callback: arg => this.toggleAlertActiveHandler(arg),
          },
          sort: 'desc',
          comparator: (valueA, valueB, nodeA, nodeB) => {
            // for the comparator, false is inactive, true is active
            // and NULL is draft.  Grid must be sorted On, Draft, Off
            const activeValueA = nodeA.data.isEnabled
            const activeValueB = nodeB.data.isEnabled
            if (activeValueA === activeValueB) return 0
            if (activeValueA === true && activeValueB === false) {
              return 1
            } else if (activeValueA === false && activeValueB === true) {
              return -1
            } else if (activeValueA === null && activeValueB === true) {
              return -1
            } else if (activeValueA === null && activeValueB === false) {
              return 1
            } else if (activeValueA === true && activeValueB === null) {
              return 1
            } else {
              return -1
            }
          },
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'isShared',
            'Shared With',
          ),
          field: 'shared',
          filter: 'agTextColumnFilter',
          suppressMovable: true,
          suppressSizeToFit: false,
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          hide: this.hideOnSharedTab,
          cellRenderer: params => {
            return `${
              params.data.isShared
                ? '<i class="icon mdi mdi-account-multiple icon-shared"></i>'
                : ''
            } ${params.value}`
          },
          width: 270,
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: [],
            items: this.sharedWithFilterRows,
            suppressFilterButton: true,
          },
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'createdBy',
            'Creator',
          ),
          field: 'createdBy',
          filter: 'agTextColumnFilter',
          suppressMovable: true,
          suppressSizeToFit: false,
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          hide: !this.hideOnSharedTab,
          width: 270,
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: [],
            items: this.getUniqueItems('createdBy'),
            suppressFilterButton: true,
          },
          cellRenderer: params => {
            return `${
              params.data.userId == this.user.userId
                ? params.value + ' (Me)'
                : params.value
            }`
          },
        },
        {
          headerName: this.getColumnTitle(this.gridColumns, 'name', 'Name'),
          field: 'name',
          filter: 'agTextColumnFilter',
          suppressMovable: true,
          suppressSizeToFit: false,
          cellRenderer: 'AscLinkCellRenderer',
          cellRendererParams: {
            disabled: this.disableFullFlightToggles,
            callback: arg => this.handleGridLinkClick(arg.baseAlertId),
          },
          minWidth: 200,
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'alertType',
            'Type',
          ),
          field: 'alertType',
          filter: 'agTextColumnFilter',
          suppressMovable: true,
          suppressSizeToFit: false,
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: [],
            items: this.getUniqueItems('alertType'),
            suppressFilterButton: true,
          },
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'airline',
            'Airline',
          ),
          field: 'airline',
          filter: 'agTextColumnFilter',
          hide: this.userAirlines.length === 1,
          suppressMovable: true,
          suppressSizeToFit: false,
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: [],
            items: this.getUniqueItems('airline'),
            suppressFilterButton: true,
          },
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'model',
            'Platform',
          ),
          field: 'model',
          filter: 'agTextColumnFilter',
          suppressMovable: true,
          suppressSizeToFit: false,
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: [],
            items: this.getUniqueItems('model'),
            suppressFilterButton: true,
          },
        },
        {
          headerComponentParams: {
            template: `<div class="ag-cell-label-container" role="presentation">
              <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
              <span ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
              <div ref="eLabel" class="ag-header-cell-label" role="presentation">
                <span ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
                <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
                <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
                <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
                <div class="ag-header-cell-text" role="columnheader" style ="display: flex;
                  flex-direction: column;align-items: center;justify-content: center;flex-grow: 1;">
                <svg viewBox="0 0 24 24" style="height: 24px; width: 24px;">
                    <path fill="#265e5b" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"></path>
                </svg>
                </div>
                <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
              </div>
            </div>`,
          },
          headerName: '',
          width: 50,
          field: 'truePositives',
          filter: 'agNumberColumnFilter',
          floatingFilterComponent: 'AscAgGridAutocompleteNumberFilter',
          suppressMovable: true,
          resizable: false,
          sortable: true,
          suppressSizeToFit: false,
          tooltipComponentParams: {
            backgroundColor: '#333',
            width: 330,
            color: '#fff',
            isHtmlContent: true,
          },
          headerTooltip: `<div class="mb-2 font-weight-bold">
                            Count of Maintenance Recommendations that successfully predicted maintenance.
                        </div>
                        <div>Count of Mx Recs with "Closed (Mx Complete)" status + Count of Mx Recs with "Part Failed Before Replacement" status</div>`,
          cellClass: 'cell-center-justify',
        },
        {
          headerComponentParams: {
            template: `<div class="ag-cell-label-container" role="presentation">
              <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
              <span ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
              <div ref="eLabel" class="ag-header-cell-label" role="presentation">
                <span ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
                <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
                <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
                <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
                <div class="ag-header-cell-text" role="columnheader" style ="display: flex;
                  flex-direction: column;align-items: center;justify-content: center;flex-grow: 1;">
                <svg viewBox="0 0 24 24" style="height: 24px; width: 24px;">
                    <path fill="#9b0022" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"></path>
                </svg>
                </div>
                <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
              </div>
            </div>`,
          },
          headerName: '',
          field: 'falsePositives',
          filter: 'agNumberColumnFilter',
          floatingFilterComponent: 'AscAgGridAutocompleteNumberFilter',
          width: 50,
          suppressMovable: true,
          resizable: false,
          sortable: true,
          tooltipComponentParams: {
            backgroundColor: '#333',
            width: 330,
            color: '#fff',
            isHtmlContent: true,
          },
          headerTooltip: `<div class="mb-2 font-weight-bold">Count of Maintenance Recommendations that did not successfully predict maintenance.</div>
                        <div>Count of Mx Recs with "No Action Required" status</div>`,
          suppressSizeToFit: false,
          cellClass: 'cell-center-justify',
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'precision',
            'Precision',
          ),
          headerComponentParams: {
            template: `<div class="ag-cell-label-container" role="presentation">
                        <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
                        <div ref="eLabel" class="ag-header-cell-label" role="presentation">
                            <span ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
                            <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
                            <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
                            <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
                            <span ref="eText" class="ag-header-cell-text" role="columnheader"></span>
                            <div>
                                <span style="color: #479D9A">
                                    <v-icon class="icon-info mdi mdi-information" style="color: #479D9A"></v-icon>
                                </span>
                            </div>
                        </div>
                        </div>`,
          },
          tooltipComponentParams: {
            backgroundColor: '#333',
            width: 310,
            color: '#fff',
            isHtmlContent: true,
          },
          headerTooltip: `<div class="d-flex flex-column justify-center align-center" style="gap: 5px">
                    <div>Precision =</div>
                    <div class="d-flex flex-column justify-center align-center" style="gap: 5px;">
                        <div>Successful Mx Recommendations</div>
                        <hr style="width:100%; border: none; height: 1px; color: white; background-color: white"/>
                        <div>Total (terminal) Mx Recommendations</div>
                    </div>
                </div>`,
          field: 'precision',
          filter: 'agNumberColumnFilter',
          floatingFilterComponent: 'AscAgGridAutocompleteNumberFilter',
          width: 170,
          sortable: true,
          suppressMovable: true,
          suppressSizeToFit: false,
          valueFormatter: params => {
            if (params.value && params.value > 0) return `${params.value}%`
          },
        },
        {
          headerName: '',
          field: '',
          cellRenderer: 'AscAgGridEllipseRenderer',
          hide: this.hideOnSharedTab,
          cellRendererParams: {
            iconOnly: 'mdi-dots-vertical',
            callback: arg => this.handleEllipsesClick(arg),
          },
          cellStyle: { padding: 0, 'text-align': 'center' },
          minWidth: 44,
          maxWidth: 44,
          width: 44,
        },
      ]
    },
    toggleAlertActiveHandler(alert) {
      this.$emit('toggleAlert', alert)
    },
    handleEllipsesClick(selectedItem) {
      if (selectedItem.selectionKey === 'Delete') {
        this.$emit('onDeleteAlert', selectedItem.selectedObject)
      } else {
        this.$emit('editAlert', {
          id: selectedItem.selectedObject.baseAlertId,
          alertType: selectedItem.selectedObject.customAlertTypeId,
        })
      }
    },
    handleGridLinkClick(baseAlertId) {
      const whichRow = this.values.filter(rowObj => {
        return rowObj.baseAlertId === baseAlertId
      })

      if (whichRow.length) {
        this.$emit('editAlert', {
          id: whichRow[0].baseAlertId,
          alertType: whichRow[0].customAlertTypeId,
        })
      }
    },
    populatePrecision(alert) {
      return alert.customAlertPerformance.precision === 0 &&
        alert.customAlertPerformance.truePositives === 0 &&
        alert.customAlertPerformance.falsePositives === 0
        ? null
        : Math.round(
            parseFloat(alert.customAlertPerformance.precision * 100) * 1e2,
          ) / 1e2
    },
    populateSharedWith(customAnalytic) {
      if (customAnalytic.isShared) {
        return `All of ${customAnalytic.airline}`
      } else {
        return 'Only Me'
      }
    },
    setSharedWithFilter() {
      let filter = []
      let count = 0
      filter.push({ id: count, text: 'Only Me', value: 'Only Me' })
      let sharedWithAirlines = this.agGridRowData.filter(x => x.isShared)
      let uniqueAirlines = new Set(sharedWithAirlines.map(x => x.airline))
      uniqueAirlines.forEach(airline => {
        filter.push({
          id: count++,
          text: `All of ${airline.toString()}`,
          value: `All of ${airline.toString()}`,
        })
      })
      return filter
    },
    showTooltip() {
      this.isTooltipVisible = true
    },
    hideTooltip() {
      this.isTooltipVisible = false
    },
    openAlertTypeDialogHandler() {
      this.$emit('openAlertTypeDialog')
    },
    handleGridReady() {
      setTimeout(() => {
        // using timeout to avoid zero width in aggrid
        this.agGridOptions.api.sizeColumnsToFit()
      }, 200)
    },
    populateTruePositive(data) {
      return data.customAlertPerformance.truePositives === 0 &&
        data.customAlertPerformance.falsePositives === 0
        ? ''
        : data.customAlertPerformance.truePositives
    },
    populateFalsePositive(data) {
      return data.customAlertPerformance.truePositives === 0 &&
        data.customAlertPerformance.falsePositives === 0
        ? ''
        : data.customAlertPerformance.falsePositives
    },
    getContextMenuItems(params) {
      const alertId = params.node.data.baseAlertId
      let alertType = ''

      switch (params.node.data.customAlertTypeId) {
        case CUSTOM_ALERT_FULL_FLIGHT_DATA_ID:
          alertType = 'fullflight'
          break
        case ADVANCED_CUSTOM_ALERTS_FULL_FLIGHT_DATA_ID:
          alertType = 'advanced-fullflight'
          break
        case ADVANCED_CUSTOM_ALERTS_DPLF_DATA_ID:
          alertType = 'advanced-dplf'
          break
        case ADVANCED_CUSTOM_ALERTS_SNAPSHOT_DATA_ID:
          alertType = 'advanced-acmf'
          break
        case CUSTOM_ALERT_SNAPSHOT_DATA_ID:
          alertType = 'acmf'
          break
      }

      if (params.column.colId === 'name') {
        return [
          {
            name: 'Open Link in New Tab',
            action: function () {
              window.open(
                `/custom-analytics/${alertType}/${alertId}?edit=true`,
                '_blank',
                'noopener',
                'noreferrer',
              )
            },
          },
          'separator',
          'copy',
          'copyWithHeaders',
          'export',
        ]
      } else {
        return [
          {
            name: 'Open Link in New Tab',
            action: function () {
              window.open(
                `/custom-analytics/${alertType}/${alertId}`,
                '_blank',
                'noopener',
                'noreferrer',
              )
            },
          },
          'separator',
          'copy',
          'copyWithHeaders',
          'export',
        ]
      }
    },
  },
}
</script>

<style lang="scss">
.asc-custom-alerts-grid--wrapper {
  position: relative;
  width: 100%;

  .asc-customAlertsNoData--wrapper {
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 150px;
    padding: 16px;

    .asc-customAlerts--noData {
      margin: 16px;
    }
  }
}

i.icon-shared {
  color: $gray-600;
  font-size: 16px;
}

.cell-center-justify {
  display: flex;
  justify-content: center;
}

.asc-custom-alerts-grid {
  position: relative;
  width: 100%;

  .ag-theme-balham {
    .ag-checkbox-input-wrapper {
      border-radius: 0;
      border: none !important;
      background-color: initial;
      position: relative;
      top: -5px;

      input,
      input:focus,
      &:focus-within {
        outline: none;
        box-shadow: none;
      }

      &::after {
        color: $gray-600;
        content: ' ';
        font: normal normal normal 24px/1 'Material Design Icons';
        border: 1px solid $gray-600;
        height: 15px;
        width: 16px;
        margin-top: 3px;
        display: block;
        outline: none;
      }

      &.ag-checked::after {
        content: '\F0E1E';
        background-color: $accent-400 !important;
        border-color: $accent-400 !important;
        color: $primary-black;
        font-size: 14px;
      }

      &.ag-indeterminate::after {
        content: ' ';
        color: $gray-600;
        font-size: 14px;
      }
    }
    .ag-header-select-all {
      .ag-checkbox-input-wrapper {
        top: 0 !important;
      }
    }
  }
}

.ag-body-viewport {
  .ag-cell[col-id='active'] {
    padding: 8px 8px 8px 16px;

    div.asc-switch[disabled] {
      &::before {
        content: '\F0028';
        font-style: normal;
        color: $semantic-error;
        font-variant: normal;
        float: left;
        margin-top: 2px;
        font-weight: normal;
        font-family: Material Design Icons, sans-serif;
        font-size: $font-size-xl;
        text-rendering: auto;
        line-height: inherit;
        -webkit-font-smoothing: antialiased;
        margin-right: 4px;
      }
    }
  }

  .asc-custom-analytics-chart--header-tooltip {
    margin-top: 2px;
    font-size: 16px;
    color: $accent-600;
  }

  .asc-customAlerts--gridDraftText {
    font-style: italic;
    font-size: $font-size-md;
    color: $gray-700;
  }
}
</style>
